import { Component } from '@theme/component';
import { fetchConfig, debounce, prefersReducedMotion } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { enqueueCartMutation } from '@theme/cart-mutation-queue';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { parseMf16Ids, getSelectedAvailabilityBakeryId } from '@theme/availability';
import {
  fetchCartJson,
  formatMf20QtyCapAlert,
  getAllowedLineQuantity,
} from '@theme/mf20-quantity-gate';
import { showThemedAlert } from '@theme/themed-alert';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Store-selection flows that dispatch `cart:update` without `sections` HTML (bubble only). */
const SILENT_CART_UPDATE_SOURCES = new Set([
  'store-selector-drawer',
  'store-selector-drawer-after-close',
  'store-locator',
  'store-detail',
]);

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /** @type {Map<string, ReturnType<typeof debounce>>} */
  #quantityDebouncers = new Map();

  #activeMutationCount = 0;

  #syncPickupSelection = () => {
    // After a cart morph (especially empty → non-empty), ensure the pickup card hydrates from localStorage.
    // The element may have been inserted by section rendering; sync is safe to call regardless.
    customElements.whenDefined('cart-pickup-store-selection').then(() => {
      for (const el of document.querySelectorAll('cart-pickup-store-selection')) {
        const pickupEl = /** @type {any} */ (el);
        if (typeof pickupEl.syncFromStorage === 'function') {
          pickupEl.syncFromStorage();
        }
      }
    });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    for (const debouncedUpdate of this.#quantityDebouncers.values()) {
      debouncedUpdate.cancel();
    }
    this.#quantityDebouncers.clear();
  }

  /**
   * Rejects events from the other cart surface before they can reset this
   * component's debounce timer, then debounces independently per stable key.
   *
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #handleQuantitySelectorUpdate = (event) => {
    const target = event.target;
    if (target instanceof Node && !this.contains(target)) return;

    const { quantity, cartLine: line, cartLineKey } = event.detail;
    const key = String(cartLineKey || '').trim();
    if (!line || !key) {
      void this.#recoverCartSections();
      return;
    }

    let debouncedUpdate = this.#quantityDebouncers.get(key);
    if (!debouncedUpdate) {
      debouncedUpdate = debounce((nextQuantity, nextLine) => {
        this.#quantityDebouncers.delete(key);
        this.#onQuantityChange(nextQuantity, nextLine, key);
      }, 300);
      this.#quantityDebouncers.set(key, debouncedUpdate);
    }

    debouncedUpdate(quantity, line);
  };

  /**
   * @param {number} quantity
   * @param {number} line
   * @param {string} key
   */
  #onQuantityChange(quantity, line, key) {
    if (quantity === 0) {
      this.#removeLineItem(line, key);
      return;
    }

    void this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} event - The activating event.
   */
  onLineItemRemove(line, event) {
    const target = event?.target;
    const row = target instanceof Element ? target.closest('[data-key]') : null;
    const key = row instanceof HTMLElement ? String(row.dataset.key || '').trim() : '';
    if (!key) {
      void this.#recoverCartSections();
      return;
    }

    this.#removeLineItem(line, key);
  }

  /**
   * @param {number} line
   * @param {string} key
   */
  #removeLineItem(line, key) {
    const pendingQuantityUpdate = this.#quantityDebouncers.get(key);
    pendingQuantityUpdate?.cancel();
    this.#quantityDebouncers.delete(key);

    void this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#findCartItemRow(key);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Animate optimistically, but never physically remove these nodes. The
    // authoritative section morph owns removal so an animation callback cannot
    // delete a row node that the morph has recycled into another product.
    rowsToRemove.forEach((row) => {
      if (prefersReducedMotion()) return;

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} config.key - Stable Shopify cart line-item key.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    try {
      await enqueueCartMutation(async () => {
        const shimmerElements = this.#startAdjustmentShimmer(config.key);
        try {
          await this.#performQuantityUpdate(config);
        } catch (error) {
          await this.#recoverCartSections();
          throw error;
        } finally {
          for (const element of shimmerElements) {
            element.removeAttribute('shimmer');
          }
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * @param {{line: number, key: string, quantity: number, action: string}} config
   */
  async #performQuantityUpdate(config) {
    const { line, key, quantity } = config;
    const row = this.#findCartItemRow(key);
    if (!row) throw new Error(`Cart row not found for key ${key}`);

    if (quantity !== 0) {
      const cartPre = await fetchCartJson();
      const lineItem = cartPre?.items?.find((item) => item.key === key);
      if (lineItem && parseMf16Ids(row.dataset.availabilityMf16).length > 0) {
        const productId = row.dataset.productId || String(lineItem.product_id ?? '');
        const B = getSelectedAvailabilityBakeryId();
        const { allowed, mfCap } = getAllowedLineQuantity(
          row.dataset.availabilityMf16,
          row.dataset.availabilityMf20,
          productId,
          B,
          cartPre,
          lineItem.key,
          row.dataset.availabilityMf36
        );
        if (quantity > allowed) {
          // Fire-and-forget the themed popup; the qty reset below runs immediately.
          void showThemedAlert(formatMf20QtyCapAlert(mfCap));
          const input = row.querySelector('quantity-selector-component input[ref="quantityInput"]');
          if (input instanceof HTMLInputElement) {
            input.value = String(lineItem.quantity ?? 1);
          }
          return;
        }
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    }

    /** @type {{ quantity: number, sections: string, sections_url: string, id: string }} */
    const changePayload = {
      id: key,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    const response = await fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig('json', { body: JSON.stringify(changePayload) })
    );
    const parsedResponse = await response.json();

    if (!response.ok || parsedResponse.errors) {
      this.#handleCartError(key, line, parsedResponse);
      throw new Error(`Cart change failed with status ${response.status}`);
    }

    const ownSectionHtml = parsedResponse.sections?.[this.sectionId];
    if (typeof ownSectionHtml !== 'string') {
      throw new Error(`Cart section ${this.sectionId} was not rendered`);
    }

    const newCartItemCount = Number(parsedResponse.item_count);
    this.dispatchEvent(
      new CartUpdateEvent(parsedResponse, this.sectionId, {
        itemCount: Number.isFinite(newCartItemCount) ? newCartItemCount : 0,
        source: 'cart-items-component',
        sections: parsedResponse.sections,
      })
    );

    await morphSection(this.sectionId, ownSectionHtml);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {string} key - Stable line-item key.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {unknown} parsedResponseText.errors - The errors.
   */
  #handleCartError = (key, line, parsedResponseText) => {
    const row = this.#findCartItemRow(key);
    const quantityInput = row?.querySelector('input[ref="quantityInput"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError =
      row?.querySelector('.cart-item__error-text') || this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer =
      row?.querySelector('.cart-items__error') || this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent =
        typeof parsedResponseText.errors === 'string'
          ? parsedResponseText.errors
          : Theme.translations.cartError || 'Unable to update the cart.';
    }
    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const cartItemsHtml = event.detail.resource?.sections?.[this.sectionId];
      if (typeof cartItemsHtml === 'string') {
        void morphSection(this.sectionId, cartItemsHtml);
      } else {
        void sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
      queueMicrotask(this.#syncPickupSelection);
      return;
    }
    if (event.target === this) return;

    const source = String(event.detail?.data?.source || '');
    const itemCount = event.detail?.data?.itemCount;
    const cartItemsHtml = event.detail.data?.sections?.[this.sectionId];

    if (SILENT_CART_UPDATE_SOURCES.has(source)) {
      if (cartItemsHtml) {
        morphSection(this.sectionId, cartItemsHtml);
        queueMicrotask(this.#syncPickupSelection);
        return;
      }
      if (itemCount === 0) {
        void this.#refreshSectionAfterSilentClear();
      }
      return;
    }

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    queueMicrotask(this.#syncPickupSelection);
  };

  /**
   * Re-fetch cart section after `/cart/clear.js` (cache-bust) then publish count once for the bubble.
   * @private
   */
  async #refreshSectionAfterSilentClear() {
    const url = new URL(window.location.href);
    url.searchParams.set('_cart_sync', String(Date.now()));
    const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
    morphSection(this.sectionId, html);
    this.#publishCartCountFromDom();
    queueMicrotask(this.#syncPickupSelection);
  }

  /** @private */
  #readCartItemCountFromDom() {
    const el = document.querySelector(
      `cart-items-component[data-section-id="${this.sectionId}"] [ref="cartItemCount"]`
    );
    const parsed = parseInt(el?.textContent ?? '', 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /** Single bubble update after silent clear morph (avoids 0 → stale → 0 flash). @private */
  #publishCartCountFromDom() {
    const itemCount = this.#readCartItemCountFromDom();
    document.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
      })
    );
  }

  /**
   * @param {string} key
   * @returns {HTMLTableRowElement | undefined}
   */
  #findCartItemRow(key) {
    if (!Array.isArray(this.refs.cartItemRows)) return undefined;
    return this.refs.cartItemRows.find((row) => row.dataset.key === key);
  }

  /**
   * Shimmer only the changing line price and estimated total, on every visible
   * cart surface. The quantity remains readable while Shopify confirms it.
   *
   * @param {string} key
   * @returns {Set<HTMLElement>}
   */
  #startAdjustmentShimmer(key) {
    const shimmerElements = new Set();

    for (const component of document.querySelectorAll('cart-items-component')) {
      for (const row of component.querySelectorAll('[data-key]')) {
        if (!(row instanceof HTMLElement) || row.dataset.key !== key) continue;
        const linePrice = row.querySelector('.cart-items__price text-component');
        if (linePrice instanceof HTMLElement) shimmerElements.add(linePrice);
      }

      const total = component.querySelector('[ref="cartTotal"]');
      if (total instanceof HTMLElement) shimmerElements.add(total);
    }

    for (const element of shimmerElements) {
      const textComponent = /** @type {TextComponent} */ (element);
      textComponent.shimmer();
    }

    return shimmerElements;
  }

  /**
   * A Cart API mutation can succeed even when bundled section rendering fails.
   * Never retry the mutation; re-fetch every visible cart section instead.
   */
  async #recoverCartSections() {
    const sectionIds = new Set();
    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }

    await Promise.allSettled(
      Array.from(sectionIds, (sectionId) => sectionRenderer.renderSection(sectionId, { cache: false }))
    );
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.#activeMutationCount += 1;
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.#activeMutationCount = Math.max(0, this.#activeMutationCount - 1);
    if (this.#activeMutationCount > 0) return;
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
